import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  StyleSheet,
  Platform,
  TouchableOpacity,
  Text,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useAppContext } from '../contexts/AppContext';
import { apiService } from '../services/api';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useTypography } from '../hooks/useTypography';
import { useIsMobileBrowser } from '../hooks/useIsMobileBrowser';
import { AvatarMenu } from './AvatarMenu';
import { NotificationsDropdown } from './NotificationsDropdown';
import { Logo } from './Logo';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useHardwareBackAction } from '../navigation/hardwareBack';
import { useAppStore } from '../store/appStore';
import { useGlobalSearchStore } from '../store/globalSearchStore';

const isWeb = Platform.OS === 'web';

interface HeaderProps {
  title?: string;
  showBackButton?: boolean;
  onBackPress?: () => void;
  showNotifications?: boolean;
  showMessages?: boolean;
  onNotificationPress?: () => void;
  onMessagePress?: () => void;
  // Navigation props for web
  activeTab?: string;
  onTabPress?: (tabId: string) => void;
  showNavigation?: boolean;
  /** Keep the brand logo visible alongside the back button + title, instead of the default
   * back-button-replaces-logo behavior. For screens reached mid-app (not a tab root) that still
   * want the logo present — e.g. a "complete your profile" sub-flow. Defaults to false so every
   * existing showBackButton caller keeps its current (logo-hidden) look. */
  showLogoWithBack?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  title,
  showBackButton = false,
  showLogoWithBack = false,
  onBackPress,
  showNotifications = true,
  showMessages = true,
  onNotificationPress,
  onMessagePress,
  // Navigation props for web
  activeTab = 'home',
  onTabPress,
  showNavigation = false,
}) => {
  const styles = useThemedStyles(getStyles);
  useHardwareBackAction(showBackButton ? onBackPress : undefined);
  const { handleChatNavigation, navigateTo } = useAppContext();
  const { t, isRTL, locale } = useLocale();
  const rtl = useRTLStyles();
  const typo = useTypography();
  const isMobileBrowser = useIsMobileBrowser();
  const businessId = useAppStore(s => s.currentBusiness?._id);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  
  // Use the context function if no onMessagePress is provided
  const handleMessagePress = onMessagePress || handleChatNavigation;
  
  // Default notification press handler - navigate to notifications screen
  const handleNotificationPress = onNotificationPress || (() => {
    console.log('🔔 [Header] Navigating to notifications screen');
    navigateTo('notifications');
  });

  // Fetch unread notification count periodically
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await apiService.authenticatedRequest(
          '/api/notifications?unreadOnly=true&limit=1',
          { method: 'GET' }
        );
        
        if (response.data && typeof response.data === 'object' && 'unreadCount' in response.data) {
          setUnreadNotificationCount((response.data as any).unreadCount);
        }
      } catch (error) {
        // Silently fail - unread count is not critical
        console.log('📊 [Header] Could not fetch unread count (user may not be logged in)');
      }
    };
    
    // ⚠️ ANDROID FIX: Add small delay on first fetch to ensure token is ready
    const { Platform } = require('react-native');
    const initialDelayMs = Platform.OS === 'android' ? 100 : 0;
    
    const timeoutId = setTimeout(() => {
      fetchUnreadCount();
    }, initialDelayMs);
    
    const interval = setInterval(fetchUnreadCount, 30000); // Every 30 seconds
    
    // Listen for Socket.IO notification events to update badge immediately
    const { socketService } = require('../services/socket.service');
    const handleNotification = () => {
      console.log('🔔 [Header] Notification received, refreshing count');
      fetchUnreadCount();
    };
    socketService.on('app-notification', handleNotification);
    
    return () => {
      clearTimeout(timeoutId);
      clearInterval(interval);
      socketService.off('app-notification', handleNotification);
    };
  }, []);

  // Fetch unread conversations count from API instead of relying on store
  const [unreadConversationsCount, setUnreadConversationsCount] = useState(0);

  // Fetch unread conversations count from API
  const fetchUnreadConversationsCount = async () => {
    try {
      const response = await apiService.getUnifiedConversations({
        limit: 1,
        offset: 0,
        status: 'active'
      });
      
      if (response.data && response.data.conversations) {
        // Count conversations with unreadCount > 0
        const unreadCount = response.data.conversations.filter((conv: any) => (conv.unreadCount || 0) > 0).length;
        setUnreadConversationsCount(unreadCount);
        console.log('🔍 [Header] Fetched unread conversations count:', unreadCount);
      }
    } catch (error) {
      console.log('📊 [Header] Could not fetch unread conversations count:', error);
    }
  };

  // Fetch unread count on component mount
  useEffect(() => {
    fetchUnreadConversationsCount();
  }, []);

  // Listen for new messages to update message badge in real-time
  useEffect(() => {
    const { socketService } = require('../services/socket.service');
    
    const handleNewMessage = (data: any) => {
      console.log('📨 [Header] New message received, updating message badge');
      // Fetch fresh unread count from API
      fetchUnreadConversationsCount();
    };
    
    const handleConversationUpdate = (data: any) => {
      console.log('📨 [Header] Conversation updated, updating message badge');
      // Fetch fresh unread count from API
      fetchUnreadConversationsCount();
    };
    
    const handleMarkedAsRead = (data: any) => {
      console.log('✅ [Header] Conversation marked as read, updating message badge');
      // Fetch fresh unread count from API
      fetchUnreadConversationsCount();
    };
    
    socketService.onNewMessage(handleNewMessage);
    socketService.onConversationUpdate(handleConversationUpdate);
    socketService.on('conversationMarkedAsRead', handleMarkedAsRead);
    
    return () => {
      socketService.off('new-message', handleNewMessage);
      socketService.off('conversation-update', handleConversationUpdate);
      socketService.off('conversationMarkedAsRead', handleMarkedAsRead);
    };
  }, []);






  /** Standard horizontal web nav row — desktop-web only (mobile-web uses
   * BottomTabs instead, matching native, see ScreenLayout/HomeScreen wiring).
   * Icon+text links with an underline active-state, not the old bottom-tab-
   * bar-styled strip this replaces. "More"'s non-account destinations moved
   * into AvatarMenu; account items (settings/logout/etc.) live there too. */
  const renderDesktopNav = () => {
    if (!isWeb || isMobileBrowser || !showNavigation) return null;

    const tabs = [
      { id: 'home', label: t('tab.home'), icon: 'home-outline' },
      { id: 'contacts', label: t('tab.contacts'), icon: 'person-outline' },
      { id: 'calendar', label: t('tab.calendar'), icon: 'calendar-outline' },
      { id: 'orders', label: t('tab.orders'), icon: 'receipt-outline' },
    ];

    return (
      <View style={styles.desktopNav}>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <TouchableOpacity
              key={tab.id}
              style={[styles.desktopNavItem, isActive && styles.desktopNavItemActive]}
              onPress={() => {
                const screenId = tab.id === 'calendar' ? 'business-calendar' : tab.id;
                onTabPress?.(screenId);
              }}
            >
              <Ionicons
                name={tab.icon as any}
                size={18}
                color={isActive ? (colors.brand.primary as string) : colors.textLight}
              />
              <Text style={[
                styles.desktopNavLabel,
                { fontFamily: isActive ? typo.fontFamily.bold : typo.fontFamily.medium },
                isActive && styles.desktopNavLabelActive,
              ]}>
                {tab.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    );
  };

  const startSection = (
    <View style={styles.startSection}>
      {showBackButton && showLogoWithBack && (
        <TouchableOpacity onPress={() => navigateTo('home')} activeOpacity={0.8} style={[styles.logoWithBack, rtl.marginEnd(spacing.md)]}>
          <Logo size="small" />
        </TouchableOpacity>
      )}
      {showBackButton ? (
        <View style={styles.titleSection}>
          <TouchableOpacity
            style={[styles.backButton, rtl.marginEnd(spacing.sm)]}
            onPress={onBackPress}
          >
            <Ionicons name={rtl.mirrorIcon('arrow-back') as any} size={24} color={colors.text.primary as string} />
          </TouchableOpacity>
          {title && (
            <Text style={[styles.title, { fontFamily: typo.fontFamily.bold, textAlign: rtl.textStart }]}>{title}</Text>
          )}
        </View>
      ) : (
        <TouchableOpacity onPress={() => navigateTo('home')} activeOpacity={0.8}>
          <Logo size="small" />
        </TouchableOpacity>
      )}
    </View>
  );

  const isMac = isWeb && typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || '');
  const openSearch = () => useGlobalSearchStore.getState().show();
  const endSection = (
    <View style={styles.endSection}>
      {!!businessId && (isWeb && !isMobileBrowser ? (
        <TouchableOpacity accessibilityRole="search" accessibilityLabel={t('search.open')} style={[styles.searchPill, { flexDirection: rtl.rowDirection }]} onPress={openSearch}>
          <Ionicons name="search" size={16} color={colors.text.secondary as string} />
          <Text style={[styles.searchPillText, { textAlign: rtl.textStart }]} numberOfLines={1}>{t('search.pill')}</Text>
          <Text style={styles.searchKbd}>{isMac ? '⌘K' : 'Ctrl K'}</Text>
        </TouchableOpacity>
      ) : (
        <TouchableOpacity accessibilityRole="search" accessibilityLabel={t('search.open')} style={styles.iconContainer} onPress={openSearch}>
          <Ionicons name="search-outline" size={22} color={colors.text.primary as string} />
        </TouchableOpacity>
      ))}
      {showMessages && (
        <TouchableOpacity style={styles.iconContainer} onPress={handleMessagePress}>
          <View style={styles.iconWrapper}>
            <Ionicons name="chatbubbles-outline" size={22} color={colors.text.primary as string} />
            {unreadConversationsCount > 0 && (
              <View style={[styles.notificationBadge, rtl.isRTL ? { left: -4, right: undefined } : {}]}>
                <Text style={[styles.notificationBadgeText, { fontFamily: typo.fontFamily.bold }]}>
                  {unreadConversationsCount > 99 ? '99+' : unreadConversationsCount}
                </Text>
              </View>
            )}
          </View>
        </TouchableOpacity>
      )}
      {showNotifications && (
        isWeb && !isMobileBrowser ? (
          <NotificationsDropdown unreadCount={unreadNotificationCount} onUnreadCountChange={setUnreadNotificationCount} />
        ) : (
          <TouchableOpacity style={styles.iconContainer} onPress={handleNotificationPress}>
            <View>
              <Ionicons name="notifications-outline" size={22} color={colors.text.primary as string} />
              {unreadNotificationCount > 0 && (
                <View style={[styles.notificationBadge, rtl.isRTL ? { left: -4, right: undefined } : {}]}>
                  <Text style={[styles.notificationBadgeText, { fontFamily: typo.fontFamily.bold }]}>
                    {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                  </Text>
                </View>
              )}
            </View>
          </TouchableOpacity>
        )
      )}
      {isWeb && !isMobileBrowser && showNavigation && <AvatarMenu />}
    </View>
  );

  return (
    <View style={styles.container}>
      {/* Main Header Bar — on native swap child order for RTL; on web dir=rtl handles it */}
      <View style={styles.headerBar}>
        {(isRTL && !isWeb) ? <>{endSection}{startSection}</> : (
          <>
            {startSection}
            {renderDesktopNav()}
            {endSection}
          </>
        )}
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    backgroundColor: colors.brand.chrome,
    ...(isWeb && {
      // Web-specific styling without absolute positioning
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
    }),
  },
  headerBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    minHeight: 60,
  },
  startSection: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  titleSection: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoWithBack: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    padding: spacing.sm,
  },
  title: {
    fontSize: typography.fontSize.lg,
    color: colors.text.primary,
  },
  endSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  notificationBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#FF3B30',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
    borderWidth: 2,
    borderColor: colors.brand.chrome,
  },
  notificationBadgeText: {
    fontSize: 10,
    color: colors.white,
    textAlign: 'center',
  },
  iconWrapper: {
    position: 'relative',
  },
  searchPill: {
    alignItems: 'center',
    gap: spacing.sm,
    minWidth: 200,
    paddingHorizontal: spacing.md,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.secondary,
  },
  searchPillText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
  },
  searchKbd: {
    fontSize: 11,
    color: colors.text.secondary,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 5,
    paddingHorizontal: 5,
    paddingVertical: 1,
  },
  desktopNav: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xl,
  },
  desktopNavItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: spacing.sm,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  desktopNavItemActive: {
    borderBottomColor: colors.brand.primary,
  },
  desktopNavLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
  },
  desktopNavLabelActive: {
    color: colors.brand.primary,
  },
});
